import type { TrainingModel } from "./TrainingModel";
import type {
  Training,
  TrainingsPaginationRequest,
  TrainingSearchRequest,
  TrainingAlertGroup,
  TrainingTemplate,
  TrainingTemplatesPaginationRequest,
  CreateTrainingTemplatePayload,
  TrainingTemplateExternalOptionsResponse,
  ExternalProviderType,
  TrainingCourse,
  Session,
} from "./Training.typed";
import type { CourseTemplate } from "../followup/Followup.typed";
import type { TheoryTestTemplate } from "../theoryTests/TheoryTest.typed";
import type { TrainingMailEvent } from "../mailTemplates/MailTemplate.typed";
import type { PaginatedResponse } from "../zekay/pagination";

export class TrainingAdapter {
  constructor(private model: TrainingModel) {}

  async getDoneTrainings(
    paginationParams?: TrainingsPaginationRequest,
  ): Promise<PaginatedResponse<Training> | Training[]> {
    return this.model.getDoneTrainings(paginationParams);
  }

  async getTrainingAlerts(): Promise<TrainingAlertGroup[]> {
    return this.model.getTrainingAlerts();
  }

  async createTraining(training: Training): Promise<Training> {
    return this.model.createTraining(training);
  }

  async deleteTraining(training: Training): Promise<void> {
    return this.model.deleteTraining(training);
  }

  async updateTraining(t: Training): Promise<void> {
    return this.model.updateTraining(t);
  }

  async batchIncrementTrainingSessions(trainingIds: number[]): Promise<number> {
    return this.model.batchIncrementTrainingSessions(trainingIds);
  }

  async sendTrainingMail(trainingId: number, event: TrainingMailEvent): Promise<void> {
    return this.model.sendTrainingMail(trainingId, event);
  }

  async validateUserQualification(t: Training): Promise<void> {
    return this.model.validateUserQualification(t);
  }

  async updateTrainingFile(trainingId: number, formData: FormData): Promise<void> {
    return this.model.updateTrainingFile(trainingId, formData);
  }

  async deleteTrainingFile(trainingId: number, type: string): Promise<void> {
    return this.model.deleteTrainingFile(trainingId, type);
  }

  async getTrainingTemplates(
    paginationParams?: TrainingTemplatesPaginationRequest,
  ): Promise<PaginatedResponse<TrainingTemplate> | TrainingTemplate[]> {
    return this.model.getTrainingTemplates(paginationParams);
  }

  async updateTrainingTemplate(tt: TrainingTemplate): Promise<void> {
    return this.model.updateTrainingTemplate(tt);
  }

  async deleteTrainingTemplate(id: number): Promise<void> {
    return this.model.deleteTrainingTemplate(id);
  }

  async createTrainingTemplate(
    payload: CreateTrainingTemplatePayload,
  ): Promise<TrainingTemplate | void> {
    return this.model.createTrainingTemplate(payload);
  }

  async getTrainingTemplatesExternalOptions(
    type: ExternalProviderType,
  ): Promise<TrainingTemplateExternalOptionsResponse> {
    return this.model.getTrainingTemplatesExternalOptions(type);
  }

  async getTrainingsByTemplateId(templateId: string): Promise<Training[]> {
    return this.model.getTrainingsByTemplateId(templateId);
  }

  async getTrainingsByTemplateIdAndStatus(
    templateId: string,
    status: number,
    paginationParams?: TrainingsPaginationRequest,
  ): Promise<PaginatedResponse<Training> | Training[]> {
    return this.model.getTrainingsByTemplateIdAndStatus(templateId, status, paginationParams);
  }

  async searchTrainings(
    searchRequest: TrainingSearchRequest,
  ): Promise<PaginatedResponse<Training> | Training[]> {
    return this.model.searchTrainings(searchRequest);
  }

  async getTrainingByUserTemplateId(
    templateId: number,
    userId: number,
  ): Promise<Training | undefined> {
    return this.model.getTrainingByUserTemplateId(templateId, userId);
  }

  async getCourseTemplate(id: number): Promise<CourseTemplate | undefined> {
    return this.model.getCourseTemplate(id);
  }

  async getTheoryTestTemplate(templateId: number): Promise<TheoryTestTemplate | undefined> {
    return this.model.getTheoryTestTemplate(templateId);
  }

  async listOnlineTheoryTestTemplateIds(): Promise<number[]> {
    return this.model.listOnlineTheoryTestTemplateIds();
  }

  async createCourseTemplate(ct: CourseTemplate): Promise<void> {
    return this.model.createCourseTemplate(ct);
  }

  async updateCourseTemplate(ct: CourseTemplate): Promise<void> {
    return this.model.updateCourseTemplate(ct);
  }

  async createSession(session: Session): Promise<void> {
    return this.model.createSession(session);
  }

  async updateSession(s: Session): Promise<void> {
    return this.model.updateSession(s);
  }

  async generateSessionPDF(templateId: number, customerId: number): Promise<void> {
    return this.model.generateSessionPDF(templateId, customerId);
  }

  async deleteSession(templateId: number, customerId: number, number: number): Promise<void> {
    return this.model.deleteSession(templateId, customerId, number);
  }

  async updateTheoryTestTemplate(template: TheoryTestTemplate): Promise<void> {
    return this.model.updateTheoryTestTemplate(template);
  }

  async createTheoryTestTemplate(template: TheoryTestTemplate): Promise<void> {
    return this.model.createTheoryTestTemplate(template);
  }

  async uploadTrainingTemplateResource(
    templateId: number,
    field: "course1" | "course2" | "theory" | "agreement" | "disclaimer",
    file: Blob,
  ): Promise<TrainingTemplate> {
    return this.model.uploadTrainingTemplateResource(templateId, field, file);
  }

  async listTrainingCourses(templateId: number): Promise<TrainingCourse[]> {
    return this.model.listTrainingCourses(templateId);
  }

  async addTrainingCourse(templateId: number, file: Blob, name?: string): Promise<TrainingCourse> {
    return this.model.addTrainingCourse(templateId, file, name);
  }

  async deleteTrainingCourse(templateId: number, courseId: string): Promise<void> {
    return this.model.deleteTrainingCourse(templateId, courseId);
  }

  async replaceTrainingCourses(templateId: number, courses: TrainingCourse[]): Promise<void> {
    return this.model.replaceTrainingCourses(templateId, courses);
  }
}
